import { useState } from 'react';
import { Loader2, MessageCircle } from 'lucide-react';
import { ModalEnviarWhatsApp } from '../orcamento/ModalEnviarWhatsApp';
import gerarExtratoParcelamento from '../../utils/gerarExtratoParcelamento';
import { jsPdfDocToJpegs } from '../../utils/pdfToJpeg';

// Tipos da cotacao de parcelamento no cartao (so EXIBE). Espelham o retorno dos endpoints
// /api/cartao/parcelamento (MP/Point) e /api/caixa/parcelamento (PagBank). Fonte UNICA: tanto o
// Orcamento (Enviar ao Caixa) quanto o Caixa (Finalizar Venda) importam daqui.
export interface ParcelaCalculada {
  n: number;
  total: number; // centavos (ja com imposto a partir do 2x)
  valor_parcela: number; // centavos
  sem_juros: boolean;
  imposto?: number; // centavos — imposto embutido no total (0 no 1x)
  total_sem_imposto?: number; // centavos
  juros_mes?: number; // taxa de juros ao mes (a.m.) em %; 0/undefined = sem juros
}
export interface ResultadoParcelamento {
  base_pix: number;
  tabela: number;
  sem_juros_ate: number;
  parcelas: ParcelaCalculada[];
}
export type EstadoParcelamento = { loading: boolean; erro?: string; data?: ResultadoParcelamento };

interface Props {
  // Parcelas JA cotadas pelo pai (esta peca so formata a imagem — nunca recalcula).
  parcelas: ParcelaCalculada[];
  // Provedor que cotou (so rotula a imagem): 'Mercado Pago' (Point) ou 'PagBank'.
  provedor: 'Mercado Pago' | 'PagBank';
  // Contexto do orcamento/pedido pro envio pelo cano do orcamento.
  orcamentoId: number | null;
  numero: string | null | undefined;
  clienteId: number;
  clienteNome: string;
  operador?: string; // carimbo na linha do provedor
  // Rotulo do documento na legenda/nome do arquivo: 'Orcamento' (default) no balcao;
  // 'Titulo' no Contas a Receber (Receber Titulo). So muda o texto — nunca o calculo.
  rotuloDocumento?: string;
  // Como reportar falha: Caixa passa toast.erro; Orcamento (modal custom) usa alert (default).
  onErro?: (msg: string) => void;
}

// Botao "Enviar simulacao por WhatsApp": gera a imagem do extrato de parcelamento (mesmos dados JA
// cotados — so formata) e abre o ModalEnviarWhatsApp com ela pronta, reusando o cano de envio de
// imagem do orcamento. Peca UNICA usada no Enviar ao Caixa (orcamento) e no Finalizar Venda (caixa).
export function SimulacaoParcelamentoWhatsApp({
  parcelas,
  provedor,
  orcamentoId,
  numero,
  clienteId,
  clienteNome,
  operador,
  rotuloDocumento = 'Orçamento',
  onErro,
}: Props) {
  const [gerando, setGerando] = useState(false);
  const [simulacao, setSimulacao] = useState<{ imagens: string[]; filename: string; caption: string } | null>(null);

  async function enviar() {
    if (gerando) return;
    setGerando(true);
    try {
      const doc = await gerarExtratoParcelamento({
        numeroOrcamento: numero || 'S/N',
        provedor,
        operador,
        parcelas: parcelas.map((x) => ({ n: x.n, total: x.total, valor_parcela: x.valor_parcela, sem_juros: x.sem_juros })),
      });
      const imagens = await jsPdfDocToJpegs(doc);
      setSimulacao({
        imagens,
        filename: `simulacao-parcelamento-${numero || rotuloDocumento.toLowerCase()}.jpg`,
        caption: `Simulação de parcelamento — ${rotuloDocumento} ${numero || ''}`.trim(),
      });
    } catch (e: any) {
      (onErro ?? ((m: string) => alert(m)))(e?.message || 'Falha ao gerar a imagem da simulação');
    } finally {
      setGerando(false);
    }
  }

  return (
    <>
      <button
        type="button"
        data-componente="014"
        onClick={() => void enviar()}
        disabled={gerando}
        style={{
          background: 'transparent', color: '#166534', border: '1px solid #166534',
          padding: '6px 12px', borderRadius: 6, fontWeight: 600, fontSize: 12.5,
          cursor: gerando ? 'wait' : 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          opacity: gerando ? 0.7 : 1,
        }}
      >
        {gerando ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />}
        {gerando ? 'Gerando imagem…' : 'Enviar simulação por WhatsApp'}
      </button>
      {simulacao && (
        <ModalEnviarWhatsApp
          orcamentoId={orcamentoId}
          numero={numero ?? undefined}
          clienteId={clienteId}
          clienteNome={clienteNome}
          imagensProntas={simulacao.imagens}
          filenamePronto={simulacao.filename}
          captionPersonalizado={simulacao.caption}
          onClose={() => setSimulacao(null)}
        />
      )}
    </>
  );
}
